import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, Loader2, Smartphone, AlertCircle } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { api } from '../services/api';

const TABS = [
  { id: 'email', label: 'Por e-mail', icon: Mail },
  { id: 'sms', label: 'Por SMS', icon: Smartphone },
];

function PasswordStrength({ value }) {
  if (!value) return null;
  const checks = [
    { ok: value.length >= 6, label: 'Mínimo 6 caracteres' },
    { ok: /[A-Z]/.test(value), label: 'Uma letra maiúscula' },
    { ok: /[0-9]/.test(value), label: 'Um número' },
  ];
  return (
    <ul className="text-xs space-y-1 mt-1">
      {checks.map((c) => (
        <li key={c.label} className={c.ok ? 'text-green-600' : 'text-gray-400'}>
          {c.ok ? '✓' : '○'} {c.label}
        </li>
      ))}
    </ul>
  );
}

export default function EsqueciSenha() {
  const [tab, setTab] = useState('email');

  // ============ Email flow ============
  const [email, setEmail] = useState('');
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [successEmail, setSuccessEmail] = useState(false);
  const [errorEmail, setErrorEmail] = useState('');

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoadingEmail(true);
    setErrorEmail('');
    try {
      await api.post('/auth/forgot-password', { email });
      setSuccessEmail(true);
    } catch (err) {
      setErrorEmail('Erro ao processar solicitação. Tente novamente.');
    } finally {
      setLoadingEmail(false);
    }
  };

  // ============ SMS flow ============
  // Step 1 = inform email/phone → send OTP
  // Step 2 = enter OTP + new password → reset
  const [smsStep, setSmsStep] = useState(1);
  const [smsEmail, setSmsEmail] = useState('');
  const [smsPhone, setSmsPhone] = useState('');
  const [smsIdentifier, setSmsIdentifier] = useState('email'); // 'email' or 'phone'
  const [smsOtp, setSmsOtp] = useState('');
  const [smsNewPassword, setSmsNewPassword] = useState('');
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsSuccess, setSmsSuccess] = useState(false);
  const [smsError, setSmsError] = useState('');
  const [smsSentMessage, setSmsSentMessage] = useState('');

  const handleSendSMS = async (e) => {
    e.preventDefault();
    setSmsLoading(true);
    setSmsError('');
    try {
      const body = smsIdentifier === 'email' ? { email: smsEmail } : { telefone: smsPhone };
      await api.post('/auth/forgot-password-sms', body);
      setSmsSentMessage(
        smsIdentifier === 'email'
          ? 'Se a conta existir e tiver telefone cadastrado, um SMS com código foi enviado.'
          : 'Se o telefone estiver cadastrado, um SMS com código foi enviado.',
      );
      setSmsStep(2);
    } catch (err) {
      setSmsError('Erro ao processar solicitação. Tente novamente.');
    } finally {
      setSmsLoading(false);
    }
  };

  const handleResetSMS = async (e) => {
    e?.preventDefault?.();
    if (smsOtp.length !== 6) {
      setSmsError('Digite os 6 dígitos do código.');
      return;
    }
    if (smsNewPassword.length < 6) {
      setSmsError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    setSmsLoading(true);
    setSmsError('');
    try {
      await api.post('/auth/reset-password-sms', {
        email: smsEmail,
        codigo: smsOtp,
        new_password: smsNewPassword,
      });
      setSmsSuccess(true);
    } catch (err) {
      setSmsError(err?.response?.data?.detail || 'Código inválido ou expirado');
      setSmsOtp('');
    } finally {
      setSmsLoading(false);
    }
  };

  const resetSmsFlow = () => {
    setSmsStep(1);
    setSmsOtp('');
    setSmsNewPassword('');
    setSmsError('');
    setSmsSentMessage('');
  };

  // ============ Success screens ============
  if (successEmail) {
    return (
      <div className="min-h-screen bg-[var(--bg-secondary)] flex items-center justify-center p-4">
        <div className="bg-[var(--bg-primary)] rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="forgot-success-email">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">E-mail enviado!</h1>
          <p className="text-[var(--text-secondary)] mb-6">
            Se o e-mail <strong>{email}</strong> estiver cadastrado, você receberá instruções para redefinir sua senha.
          </p>
          <p className="text-sm text-[var(--text-muted)] mb-6">Verifique também a caixa de spam.</p>
          <Link to="/cadastrar" className="inline-flex items-center gap-2 text-[var(--primary)] hover:underline">
            <ArrowLeft className="w-4 h-4" /> Voltar para o login
          </Link>
        </div>
      </div>
    );
  }

  if (smsSuccess) {
    return (
      <div className="min-h-screen bg-[var(--bg-secondary)] flex items-center justify-center p-4">
        <div className="bg-[var(--bg-primary)] rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="forgot-success-sms">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Senha redefinida!</h1>
          <p className="text-[var(--text-secondary)] mb-6">
            Você já pode entrar com sua nova senha.
          </p>
          <Link
            to="/cadastrar"
            className="inline-flex items-center gap-2 bg-[var(--primary)] text-white hover:bg-[#450066] rounded-full px-6 py-2.5 font-semibold transition-all"
          >
            Ir para o login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-secondary)] flex items-center justify-center p-4">
      <div className="bg-[var(--bg-primary)] rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-[var(--primary)]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            {tab === 'sms' ? (
              <Smartphone className="w-8 h-8 text-[var(--primary)]" />
            ) : (
              <Mail className="w-8 h-8 text-[var(--primary)]" />
            )}
          </div>
          <h1 className="text-2xl font-bold">Esqueci minha senha</h1>
          <p className="text-[var(--text-secondary)] mt-2 text-sm">
            Escolha como quer recuperar o acesso à sua conta.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-6" role="tablist">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              onClick={() => {
                setTab(id);
                if (id === 'sms') resetSmsFlow();
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                tab === id ? 'bg-white text-[var(--primary)] shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid={`forgot-tab-${id}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {/* Email form */}
        {tab === 'email' && (
          <form onSubmit={handleEmailSubmit} className="space-y-4" data-testid="forgot-email-form">
            {errorEmail && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {errorEmail}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-2">E-mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="w-full px-4 py-3 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
                data-testid="forgot-email-input"
              />
            </div>
            <button
              type="submit"
              disabled={loadingEmail || !email}
              className="w-full px-4 py-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[#450066] disabled:opacity-50 flex items-center justify-center gap-2 font-semibold"
              data-testid="forgot-submit-btn"
            >
              {loadingEmail ? <><Loader2 className="w-5 h-5 animate-spin" /> Enviando...</> : 'Enviar instruções por e-mail'}
            </button>
          </form>
        )}

        {/* SMS flow */}
        {tab === 'sms' && smsStep === 1 && (
          <form onSubmit={handleSendSMS} className="space-y-4" data-testid="forgot-sms-step1">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSmsIdentifier('email')}
                className={`flex-1 py-2 text-sm rounded-lg border ${
                  smsIdentifier === 'email' ? 'border-[var(--primary)] bg-[var(--primary)]/5 text-[var(--primary)]' : 'border-[var(--border)] text-gray-600'
                }`}
              >
                Usar e-mail
              </button>
              <button
                type="button"
                onClick={() => setSmsIdentifier('phone')}
                className={`flex-1 py-2 text-sm rounded-lg border ${
                  smsIdentifier === 'phone' ? 'border-[var(--primary)] bg-[var(--primary)]/5 text-[var(--primary)]' : 'border-[var(--border)] text-gray-600'
                }`}
              >
                Usar telefone
              </button>
            </div>

            {smsError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {smsError}
              </div>
            )}

            {smsIdentifier === 'email' ? (
              <div>
                <label className="block text-sm font-medium mb-2">E-mail</label>
                <input
                  type="email"
                  value={smsEmail}
                  onChange={(e) => setSmsEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  className="w-full px-4 py-3 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
                  data-testid="forgot-sms-email-input"
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">Enviaremos um SMS para o telefone cadastrado nessa conta.</p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-2">Telefone (com DDD)</label>
                <input
                  type="tel"
                  value={smsPhone}
                  onChange={(e) => setSmsPhone(e.target.value)}
                  placeholder="(11) 98765-4321"
                  required
                  className="w-full px-4 py-3 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
                  data-testid="forgot-sms-phone-input"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={smsLoading || (smsIdentifier === 'email' ? !smsEmail : !smsPhone)}
              className="w-full px-4 py-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[#450066] disabled:opacity-50 flex items-center justify-center gap-2 font-semibold"
              data-testid="forgot-sms-send-btn"
            >
              {smsLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Enviando...</> : 'Enviar código por SMS'}
            </button>
          </form>
        )}

        {tab === 'sms' && smsStep === 2 && (
          <form onSubmit={handleResetSMS} className="space-y-4" data-testid="forgot-sms-step2">
            {smsSentMessage && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-sm text-emerald-700">
                {smsSentMessage}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">E-mail da conta</label>
              <input
                type="email"
                value={smsEmail}
                onChange={(e) => setSmsEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="w-full px-4 py-3 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
                data-testid="forgot-sms-confirm-email"
              />
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Informe o e-mail associado à conta para validar o código.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Código recebido por SMS</label>
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={smsOtp}
                  onChange={(v) => { setSmsOtp(v); if (smsError) setSmsError(''); }}
                  disabled={smsLoading}
                  autoFocus
                  inputMode="numeric"
                  data-testid="forgot-sms-otp"
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="w-10 h-12 sm:w-11 sm:h-12" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Nova senha</label>
              <input
                type="password"
                value={smsNewPassword}
                onChange={(e) => setSmsNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                required
                minLength={6}
                className="w-full px-4 py-3 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
                data-testid="forgot-sms-new-password"
              />
              <PasswordStrength value={smsNewPassword} />
            </div>

            {smsError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {smsError}
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={resetSmsFlow}
                disabled={smsLoading}
                className="flex-1 px-4 py-3 border border-[var(--border)] text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
              >
                Voltar
              </button>
              <button
                type="submit"
                disabled={smsLoading || smsOtp.length !== 6 || smsNewPassword.length < 6 || !smsEmail}
                className="flex-1 px-4 py-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[#450066] disabled:opacity-50 flex items-center justify-center gap-2 font-semibold"
                data-testid="forgot-sms-reset-btn"
              >
                {smsLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Redefinindo...</> : 'Redefinir senha'}
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link
            to="/cadastrar"
            className="inline-flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--primary)] text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar para o login
          </Link>
        </div>
      </div>
    </div>
  );
}
